import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { eventBus } from '../events/eventBus';
import type { Invoice, NewInvoice } from '../types';

interface InvoiceStore {
  invoices: Invoice[];
  loading: boolean;
  error: string | null;
  fetchInvoices: () => Promise<void>;
  fetchDealInvoices: (dealId: string) => Promise<void>;
  createInvoice: (invoice: NewInvoice) => Promise<Invoice | null>;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => Promise<Invoice | null>;
  deleteInvoice: (id: string) => Promise<boolean>;
}

export const useInvoiceStore = create<InvoiceStore>((set, get) => ({
  invoices: [],
  loading: false,
  error: null,

  fetchInvoices: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization ID
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // First try to get invoices from FattureInCloud sync table
      const { data: ficInvoices, error: ficError } = await supabase
        .from('fattureincloud_invoices')
        .select('*')
        .eq('organization_id', userProfile.organization_id)
        .order('date', { ascending: false });

      if (ficError) {
        console.warn('Error fetching FattureInCloud invoices, falling back to local invoices:', ficError);
      }

      // Also get local invoices as fallback
      const { data: localInvoices, error: localError } = await supabase
        .from('invoices')
        .select(`
          *,
          deal:deal_id (
            amount,
            client:clients (
              business_name
            )
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (localError) throw localError;

      // Combine and deduplicate invoices, prioritizing FIC invoices
      const allInvoices = [
        ...(ficInvoices || []).map((inv: any) => ({
          ...inv,
          client_name: inv.data?.entity?.name || 'Cliente',
          created_at: inv.date || inv.last_synced_at,
          amount_net: inv.data?.amount_net || inv.amount,
          amount_gross: inv.data?.amount_gross || inv.amount,
          total_amount: inv.amount,
          type: inv.type,
          source: 'fattureincloud'
        })),
        ...(localInvoices || []).filter((inv: any) => 
          !(ficInvoices || []).some((ficInv: any) => 
            ficInv.fattureincloud_id && ficInv.fattureincloud_id.toString() === inv.fattureincloud_id?.toString()
          )
        ).map((inv: any) => ({
          ...inv,
          client_name: inv.deal?.client?.business_name || 'Cliente',
          source: 'local'
        }))
      ];

      set({ invoices: allInvoices });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  fetchDealInvoices: async (dealId: string) => {
    set({ loading: true, error: null });
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('deal_id', dealId)
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ invoices: data || [] });
    } catch (error) {
      set({ error: friendlyDbError(error) });
    } finally {
      set({ loading: false });
    }
  },

  createInvoice: async (invoice) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .insert(invoice)
        .select()
        .single();

      if (error) throw error;

      // Emit workflow trigger event
      try {
        eventBus.emitEvent('invoice:created' as any, {
          invoiceId: data.id,
          dealId: data.deal_id,
          amount: data.amount,
          status: data.status,
          dueDate: data.due_date,
        });
      } catch (e) { console.warn('[InvoiceStore] Failed to emit invoice:created event:', e); }

      const invoices = get().invoices;
      set({ invoices: [data, ...invoices] });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  updateInvoice: async (id, invoice) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .update(invoice)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      const invoices = get().invoices.map(i => 
        i.id === id ? { ...i, ...data } : i
      );
      set({ invoices });
      return data;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteInvoice: async (id) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const invoices = get().invoices.filter(i => i.id !== id);
      set({ invoices });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },
}));